import type { Question, LessonUnit } from "../types/index.js";

/**
 * Derive lesson structure from question data, preserving the order
 * in which units/subunits/difficulties appear.
 */
export function buildLessonStructure(questions: Question[]): LessonUnit[] {
  const unitOrder: string[] = [];
  const subunitOrder = new Map<string, string[]>();
  const bucket = new Map<string, string[]>();

  for (const q of questions) {
    // Track unit order
    if (!unitOrder.includes(q.unit)) {
      unitOrder.push(q.unit);
    }

    // Track subunit order within unit
    if (!subunitOrder.has(q.unit)) {
      subunitOrder.set(q.unit, []);
    }
    const subs = subunitOrder.get(q.unit)!;
    if (!subs.includes(q.subunit)) {
      subs.push(q.subunit);
    }

    // Bucket by key
    const key = `${q.unit}|||${q.subunit}|||${q.difficulty}`;
    if (!bucket.has(key)) bucket.set(key, []);
    bucket.get(key)!.push(q.id);
  }

  return unitOrder
    .map((unit) => {
      const subs = subunitOrder.get(unit) ?? [];
      const lessons = subs.map((sub) => {
        const levels = [1, 2, 3]
          .map((d) => {
            const key = `${unit}|||${sub}|||${d}`;
            const ids = bucket.get(key) ?? [];
            return { difficulty: d, ids };
          })
          .filter((l) => l.ids.length > 0);
        return { subunit: sub, levels };
      });
      return { unit, lessons };
    })
    .filter((u) => u.lessons.length > 0);
}

/** Count total questions in a LessonUnit */
export function countQuestionsInUnit(unit: LessonUnit): number {
  return unit.lessons.reduce(
    (sum, lesson) =>
      sum + lesson.levels.reduce((s, l) => s + l.ids.length, 0),
    0,
  );
}
